import {
  TableSortLabel,
  Typography,
  Snackbar,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  IconButton,
} from "@mui/material";
import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  checkIfMetricsExist,
  getValueForDashBoard,
  roundStringNumberToInt,
} from "../helpers/businessleaderDashboardHelpers";
import dayjs from "dayjs";
import Glossary from "../Glossary";
import { RunRatRowsData } from "./RunRateRowsData";
import { useSelector } from "react-redux";
import { Stack } from "@mui/system";
import { saveAs } from "file-saver";
import { useSearchParams } from "react-router-dom";

interface Props {
  highLevelData: any;
}

export const RunRate: React.FC<Props> = React.memo(({ highLevelData }) => {
  // values fom business leader slice
  const {
    selectedSharedUidBui,
    selectedStartDate,
    selectedEndDate,
    selectedDaysLeftForPeriod,
  } = useSelector((state: any) => state?.businessLeaderDashBoard);

  const tableHeadings = {
    Metrics: "Metric",
    Actual: "Actual",
    Proportional_Attainment: "RR",
    Target: "Projection",
    RR: "Predicted RR",
    Insight: "Insight",
  };

  const headCells = Object.entries(tableHeadings);
  const [order, setOrder] = useState("asc") as any;
  const [orderBy, setOrderBy] = useState() as any;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage] = useState("");
  const [isValid] = useState(false);

  const selectedSharedUid = selectedSharedUidBui;
  const savedStartDate = selectedStartDate;
  const savedEndDate = selectedEndDate;

  const handleExportCSV = () => {
    const csvRows = [];

    // Extract the table headers
    let firstColumn = headCells.filter(
      (item) => item[0] !== "Metrics" && item[0] !== "Insight"
    );
    firstColumn = firstColumn.map((pair) => pair[0]) as any;
    let headers = sortedRows.map((pair: any) => pair?.name) as any;

    csvRows.push(["", ...headers]);
    // Extract the table data

    // eslint-disable-next-line
    firstColumn.map((pro: any) => {
      let rowData = [] as any;
      rowData.push(pro);
      // eslint-disable-next-line
      sortedRows.map((row: any) => {
        rowData.push(getMetricValueForCSV(row?.metric, pro));
      });
      csvRows.push([rowData.join(",")]);
    });

    // Convert CSV rows to a single string
    const csvString = csvRows.join("\n");

    // Create a blob and download the file
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "RunRate.csv");
  };

  const renderSnackbar = () => {
    return (
      <Snackbar
        sx={{ zIndex: "9999999" }}
        open={snackbarOpen}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={
            snackbarMessage.includes("Error") || isValid ? "success" : "error"
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    );
  };

  // tranfered the logic from highlevelkpi to runrate
  const daysLeftForPeriod = selectedDaysLeftForPeriod;

  let endDate = savedEndDate;

  const twoDaysAgo = dayjs().subtract(2, "day").format("YYYY-MM-DD");
  if (savedEndDate > twoDaysAgo) {
    endDate = twoDaysAgo;
  }

  let message = "";

  if (savedStartDate > twoDaysAgo) {
    message = `No Data Available, Data is only relevant for current month`;
  } else {
    message = `Data is only relevant for the period of ${dayjs(
      savedStartDate
    ).format("DD-MM-YYYY")} - ${dayjs(endDate).format(
      "DD-MM-YYYY"
    )}, Days left for the selected period: ${daysLeftForPeriod}`;
  }

  const parseValue = (value: any) => {
    if (typeof value === "string") {
      if (value.includes("%")) {
        return parseFloat(value.replace("%", ""));
      }
      if (value.includes("$")) {
        return parseFloat(value.replace("$", ""));
      } else {
        return value.toLowerCase();
      }
    } else if (value === null || !value) {
      return "";
    }
    return value;
  };

  const handleRequestSort = (property: any) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedRows = [...RunRatRowsData].sort((a, b) => {
    let valuea = "";
    let valueb = "";
    let dataa = getValueForDashBoard(a["metric"], highLevelData) as any;
    if (dataa) {
      valuea = dataa[orderBy];
    }
    let datab = getValueForDashBoard(b["metric"], highLevelData) as any;
    if (datab) {
      valueb = datab[orderBy];
    }
    const aValue = parseValue(valuea);
    const bValue = parseValue(valueb);

    if (aValue < bValue) {
      return order === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return order === "asc" ? 1 : -1;
    }
    return 0;
  });

  const getMetricValue = (metricName: string, fieldName: string) => {
    const data = getValueForDashBoard(metricName, highLevelData) as any;
    let value = data[fieldName];
    if (
      value !== null &&
      value !== 0 &&
      fieldName !== "Comment" &&
      fieldName !== "Metrics"
    ) {
      if (metricName === "cvr" && fieldName === "Metrics") {
        return "CVR%";
      } else if (fieldName === "Proportional_Attainment") {
        return roundStringNumberToInt(value * 100, true) + "%";
      } else if (metricName === "cvr") {
        return roundStringNumberToInt(value * 100, true) + "%";
      } else if (metricName !== "ftd" && metricName !== "clicks") {
        return roundStringNumberToInt(value) + "$";
      } else {
        return roundStringNumberToInt(value);
      }
    } else if (value === null) {
      return "null";
    }
    return value;
  };

  const getMetricValueForCSV = (metricName: string, fieldName: string) => {
    const data = getValueForDashBoard(metricName, highLevelData) as any;
    let value = data[fieldName];
    if (
      value !== null &&
      value !== 0 &&
      fieldName !== "Comment" &&
      fieldName !== "Metrics"
    ) {
      if (metricName === "cvr" && fieldName === "Metrics") {
        return "CVR%";
      } else if (fieldName === "Proportional_Attainment") {
        value = value * 100;
        value = Math.round(value.toFixed(2));
        return value + "%";
      } else if (metricName === "cvr") {
        value = value * 100;
        value = Math.round(value.toFixed(2));
        return value + "%";
      } else if (metricName !== "ftd" && metricName !== "clicks") {
        value = Math.round(value);
        return value + "$";
      } else {
        value = Math.round(value);
        return value;
      }
    } else if (value === null) {
      return "null";
    }
    return value;
  };
  const startDate = dayjs(savedStartDate).format("YYYY-MM-DD");
  const enDDate = dayjs(savedEndDate).format("YYYY-MM-DD");
  // const metricMapping = [
  //   { CAC: "CAC", tabMetricValue: 0 },
  //   { CLICKS: "CI", tabMetricValue: 1 },
  //   { "CPA INCOME": "CPA", tabMetricValue: 2 },
  //   { "CPA PV": "CPA", tabMetricValue: 2 },
  //   { CPC: "CI", tabMetricValue: 1 },
  //   { CVR: "CR", tabMetricValue: 3 },
  //   { FTD: "FTD", tabMetricValue: 4 },
  //   { NRPU: "PV", tabMetricValue: 5 },
  //   { PROFIT: "PT", tabMetricValue: 6 },
  //   { PV: "PV", tabMetricValue: 5 },
  //   { "RS INCOME": "RS", tabMetricValue: 7 },
  //   { "RS PV": "RS", tabMetricValue: 7 },
  //   { SPEND: "PT", tabMetricValue: 6 },
  //   { "TOTAL INCOME": "PT", tabMetricValue: 6 },
  // ] as any;
  const [searchParams] = useSearchParams();
  const selectedType = searchParams.get("selectedType");
  const dimensions = searchParams.get("selectedDimension");
  const handleMetricClick = (metric: any) => {
    const graphMapping: { [key: string]: string } = {
      CAC: "cac",
      CLICKS: "clickIn",
      "CPA INCOME": "cpa",
      "CPA PV": "cpapv",
      CPC: "cpc",
      CVR: "cvr",
      FTD: "ftd",
      NRPU: "nrpu",
      PROFIT: "profit",
      PV: "pv",
      "RS INCOME": "rs",
      "RS PV": "rsPv",
      SPEND: "spent",
      "TOTAL INCOME": "totalIncome",
    };
    const tabMapping: { [key: string]: number } = {
      CAC: 0,
      CLICKS: 1,
      "CPA INCOME": 2,
      "CPA PV": 2,
      CPC: 1,
      CVR: 3,
      FTD: 4,
      NRPU: 5,
      PROFIT: 6,
      PV: 5,
      "RS INCOME": 7,
      "RS PV": 7,
      SPEND: 6,
      "TOTAL INCOME": 6,
    };

    const graph = graphMapping[metric] || "ftd";
    const tab = tabMapping[metric] || 4;

    const params = new URLSearchParams();

    if (selectedType === "site") {
      const siteValues = selectedSharedUid.map((uid: any) =>
        uid?.value ? uid.value : uid
      );
      params.set("selectedSite", siteValues.join(","));
    } else if (
      selectedType === "organization" ||
      selectedType === "dimension"
    ) {
      if (dimensions) {
        params.set("dimension", dimensions);
      }
    }

    params.set("selectedStartDate", dayjs(startDate).format("DD-MM-YYYY"));
    params.set("selectedEndDate", dayjs(enDDate).format("DD-MM-YYYY"));

    params.set("dataSet", "click");
    params.set("view-Trends", "true");
    params.set("graph", graph);
    params.set("tab", tab.toString());

    const url = `/assets/overview/trends?${params.toString()}`;
    window.open(url, "_blank");
  };

  const getMetricsName = (metric: any) => {
    return getMetricValue(metric, "Metrics")
      ?.toString()
      ?.replace(/_/g, " ")
      ?.toUpperCase();
  };

  //radio button for horizontal and vertical
  const [display, setDisplay] = useState("Horizontal");

  const handleChange = (event: any) => {
    setDisplay(event.target.value);
  };

  const getMetricIdTrack = (metric: any) => {
    switch (metric) {
      case "ftd":
        return "63";
      case "profit":
        return "67";
      case "spend":
        return "59";
      case "clicks":
        return "61";
      case "cvr":
        return "62";
      case "RS_Income":
        return "65";
      case "RS_Income_non_neg":
        return "59";
      case "CPA_Income":
        return "64";
      case "TOTAL_Income":
        return "66";
      case "CPA_PV":
        return "71";
      case "RS_PV":
        return "72";
      case "pv":
        return "73";
      case "CAC":
        return "69";
      case "NRPU":
        return "70";
      case "cpc":
        return "68";
      default:
        return "61";
    }
  };

  return (
    <>
      {renderSnackbar()}
      <Stack
        sx={{ padding: "8px 16px" }}
        flexDirection={"row"}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography fontSize={24}>Run Rate - Target VS. Actual</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormLabel color="secondary" id="display-radio-group-label">
            <Typography fontSize={16} color="#000000DE" marginRight={1}>
              Display:
            </Typography>
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="display-radio-group-label"
            name="display"
            value={display}
            onChange={handleChange}
          >
            <FormControlLabel
              value="Horizontal"
              control={
                <Radio
                  color="secondary"
                  size="small"
                  inputProps={{ "data-tracker": "282" } as Record<string, any>}
                />
              }
              label="Horizontal"
            />
            <FormControlLabel
              value="Vertical"
              control={
                <Radio
                  color="secondary"
                  size="small"
                  inputProps={{ "data-tracker": "295" } as Record<string, any>}
                />
              }
              label="Vertical"
            />
          </RadioGroup>
        </Stack>
      </Stack>

      {/* new alert */}
      <Stack
        flexDirection={"row"}
        alignItems="center"
        gap={2}
        margin="4px 16px"
      >
        <Alert
          sx={{
            color: "#ED6C02",
            mb: 1,
            border: "1px solid #ED6C0280",
            width: "100%",
          }}
          severity="warning"
          icon={<InfoOutlinedIcon fontSize="inherit" />}
        >
          {message}
        </Alert>
        <IconButton color="secondary" size="large" data-tracker="274">
          <DownloadIcon fontSize="medium" onClick={handleExportCSV} />
        </IconButton>
      </Stack>
      {/* logic for horizontal and vertical table */}
      {display === "Horizontal" && (
        <TableContainer
          className="RunRateTableWrap"
          sx={{ boxShadow: "none", maxHeight: 400, overflowY: "auto" }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="left">
                  <></>
                </TableCell>
                {sortedRows.map((row, rowIndex) =>
                  checkIfMetricsExist(row?.metric, highLevelData) ? (
                    <TableCell
                      key={rowIndex}
                      align="left"
                      data-tracker={getMetricIdTrack(row?.metric)}
                    >
                      <Typography
                        color="secondary"
                        sx={{
                          mr: 1,
                          textDecoration: "underline",
                          justifyContent: "left",
                          "&:hover": { textDecoration: "underline" },
                          padding: 0,
                          fontSize: "14px",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          handleMetricClick(getMetricsName(row?.metric))
                        }
                      >
                        {getMetricsName(row?.metric)}
                      </Typography>
                    </TableCell>
                  ) : null
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {Object.entries(tableHeadings)
                .filter(([key]) => key !== "Metrics")
                .map(([key, label]) => (
                  <TableRow key={key}>
                    <TableCell>
                      <>{label}</>
                    </TableCell>
                    {sortedRows.map((row, rowIndex) =>
                      checkIfMetricsExist(row?.metric, highLevelData) ? (
                        <TableCell key={rowIndex + key} align="left">
                          {key === "Insight" ? (
                            <Glossary
                              metric={getMetricsName(row?.metric)}
                              comment={getMetricValue(row?.metric, "Comment")}
                            />
                          ) : (
                            getMetricValue(row?.metric, key)
                          )}
                        </TableCell>
                      ) : null
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {display === "Vertical" && (
        <TableContainer
          className="RunRateTableWrap"
          sx={{ boxShadow: "none", maxHeight: 400, overflowY: "auto" }}
        >
          <Table aria-label="collapsible table" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {headCells.map(([key, value], index) => (
                  <TableCell key={index} align="left">
                    <TableSortLabel
                      active={orderBy === key}
                      direction={orderBy === key ? order : "asc"}
                      onClick={() => handleRequestSort(key)}
                    >
                      {value}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row, rowIndex) => (
                <React.Fragment key={rowIndex + "sortedrow" + Math.random()}>
                  {checkIfMetricsExist(row?.metric, highLevelData) ? (
                    <TableRow
                      sx={{
                        "& > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      <TableCell
                        key={rowIndex + "tabelcell"}
                        align="left"
                        sx={{ py: "0px" }}
                      >
                        <>
                          <Typography
                            color="secondary"
                            sx={{
                              mr: 1,
                              textDecoration: "underline",
                              justifyContent: "left",
                              "&:hover": { textDecoration: "underline" },
                              padding: 0,
                              fontSize: "14px",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              handleMetricClick(getMetricsName(row?.metric))
                            }
                          >
                            {getMetricsName(row?.metric)}
                          </Typography>
                        </>
                      </TableCell>
                      <TableCell
                        key={rowIndex + "cell" + Math.random()}
                        align="left"
                        sx={{ py: "0px" }}
                      >
                        {getMetricValue(row?.metric, "Actual")}
                      </TableCell>
                      {/* <TableCell
                        key={rowIndex + "cell" + Math.random()}
                        align="left"
                        sx={{ py: "0px" }}
                      >
                        {getMetricValue(row?.metric, "Proportional_Target")}
                      </TableCell> */}
                      <TableCell
                        key={rowIndex + "cell" + Math.random()}
                        align="left"
                        sx={{ py: "0px" }}
                      >
                        {getMetricValue(row?.metric, "Proportional_Attainment")}
                      </TableCell>
                      <TableCell
                        key={rowIndex + "cell" + Math.random()}
                        align="left"
                        sx={{ py: "0px" }}
                      >
                        {getMetricValue(row?.metric, "Target")}
                      </TableCell>
                      <TableCell
                        key={rowIndex + "cell" + Math.random()}
                        align="left"
                        sx={{ py: "0px" }}
                      >
                        {getMetricValue(row?.metric, "RR")}
                      </TableCell>
                      <TableCell
                        key={rowIndex + "cell" + Math.random()}
                        align="left"
                        sx={{ py: "0px" }}
                      >
                        <Glossary
                          metric={getMetricsName(row?.metric)}
                          comment={getMetricValue(row?.metric, "Comment")}
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    ""
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
});
